import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { AnimateIn, SectionRow } from '../components/Layout';
import { frameworkContent } from '../data/mock';

const FrameworkPage = () => {
  return (
    <div>
      {/* Page Hero */}
      <section className="page-hero">
        <div className="page-hero-content">
          <div className="inner">
            <AnimateIn>
              <h1 className="hero-headline" style={{ marginBottom: '24px' }}>
                {frameworkContent.heroTitle}
              </h1>
            </AnimateIn>
            <AnimateIn delay={100}>
              <p className="hero-desc" style={{ maxWidth: '540px' }}>
                {frameworkContent.heroDesc}
              </p>
            </AnimateIn>
          </div>
        </div>
      </section>

      <SectionRow number="One" label="Methodology" />

      {/* Overview */}
      <section className="sec-light sec-pad-lg">
        <div className="inner">
          <AnimateIn>
            <p className="body-text" style={{ 
              fontSize: '18px',
              lineHeight: '1.7',
              maxWidth: '900px'
            }}>
              {frameworkContent.overview}
            </p>
          </AnimateIn>
        </div>
      </section>

      <SectionRow number="Two" label="Process" />

      {/* Steps */}
      <section className="sec-white sec-pad-lg">
        <div className="inner">
          <div className="grid-2">
            {frameworkContent.steps.map((step, i) => (
              <AnimateIn key={step.number} delay={i * 120}>
                <div style={{ marginBottom: '40px' }}>
                  <div style={{
                    fontSize: '48px',
                    fontFamily: "'Gambarino', Georgia, serif",
                    color: 'rgba(16,16,16,0.15)',
                    marginBottom: '12px'
                  }}>
                    {step.number}
                  </div>
                  <h3 className="heading-md" style={{ marginBottom: '12px' }}>
                    {step.title}
                  </h3>
                  <p className="body-text">{step.desc}</p>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>
      </section>

      {/* Theorem — Integrated into White Section */}
      <section className="sec-white sec-pad-lg" style={{ paddingTop: '0' }}>
        <div className="inner">
          <AnimateIn>
            <div style={{
              maxWidth: '900px',
              margin: '80px auto 0',
              padding: '60px 0',
              borderTop: '1px solid rgba(16,16,16,0.1)',
              borderBottom: '1px solid rgba(16,16,16,0.1)'
            }}>
              <p style={{
                fontFamily: "'Gambarino', Georgia, serif",
                fontSize: 'clamp(20px, 2.5vw, 36px)',
                lineHeight: '1.35',
                color: '#3E4032',
                textAlign: 'center',
                fontStyle: 'italic'
              }}>
                {frameworkContent.theorem}
              </p>
            </div>
          </AnimateIn>
        </div>
      </section>

      <SectionRow number="Three" label="Application" />

      {/* AICIL — Comprehensive Section */}
      <section className="sec-black sec-pad-lg epistemic-grid" id="aicil" style={{
        background: '#101010'
      }}>
        <div className="inner">
          {/* Title + One Sentence */}
          <AnimateIn>
            <h2 className="heading-xl" style={{ color: '#F3F3F1', marginBottom: '16px' }}>
              {frameworkContent.aicil.title}
            </h2>
          </AnimateIn>
          <AnimateIn delay={100}>
            <p style={{
              fontFamily: "'Gambarino', Georgia, serif",
              fontSize: 'clamp(20px, 2vw, 32px)',
              lineHeight: '1.35',
              color: 'rgba(243,243,241,0.7)',
              marginBottom: '80px',
              maxWidth: '900px'
            }}>
              {frameworkContent.aicil.oneSentence}
            </p>
          </AnimateIn>

          {/* Stats */}
          <div style={{ 
            display: 'flex', 
            gap: '80px', 
            marginBottom: '100px',
            flexWrap: 'wrap' 
          }}>
            {frameworkContent.aicil.stats.map((stat, i) => (
              <AnimateIn key={stat.label} delay={150 + i * 100}>
                <div>
                  <div style={{
                    fontFamily: "'Gambarino', Georgia, serif",
                    fontSize: 'clamp(48px, 7vw, 84px)',
                    lineHeight: '1',
                    color: '#F3F3F1'
                  }}>
                    {stat.value}
                  </div>
                  <div className="label-sm on-dark" style={{ marginTop: '8px' }}>
                    {stat.label}
                  </div>
                </div>
              </AnimateIn>
            ))}
          </div>

          {/* The Problem */}
          <div className="grid-2" style={{ marginBottom: '80px' }}>
            <div>
              <AnimateIn>
                <h3 className="heading-lg" style={{ color: '#F3F3F1' }}>
                  {frameworkContent.aicil.problem.heading}
                </h3>
              </AnimateIn>
            </div>
            <div>
              <AnimateIn delay={100}>
                <p className="body-text on-dark">
                  {frameworkContent.aicil.problem.body}
                </p>
              </AnimateIn>
            </div>
          </div>

          {/* How It Works */}
          <div style={{ marginBottom: '80px' }}>
            <AnimateIn>
              <h3 className="heading-lg" style={{ color: '#F3F3F1', marginBottom: '48px' }}>
                {frameworkContent.aicil.how.heading}
              </h3>
            </AnimateIn>
            <div className="grid-3">
              {frameworkContent.aicil.how.stages.map((stage, i) => (
                <AnimateIn key={stage.number} delay={i * 120}>
                  <div>
                    <div style={{
                      fontFamily: "'Gambarino', Georgia, serif",
                      fontSize: '52px',
                      color: 'rgba(243,243,241,0.15)',
                      marginBottom: '12px'
                    }}>
                      {stage.number}
                    </div>
                    <h4 className="heading-sm" style={{ color: '#F3F3F1', marginBottom: '12px' }}>
                      {stage.title}
                    </h4>
                    <p className="body-text on-dark">
                      {stage.desc}
                    </p>
                  </div>
                </AnimateIn>
              ))}
            </div>
          </div>

          {/* What Makes It Different */}
          <div className="grid-2" style={{ marginBottom: '80px' }}>
            <div>
              <AnimateIn>
                <h3 className="heading-lg" style={{ color: '#F3F3F1' }}>
                  {frameworkContent.aicil.different.heading}
                </h3>
              </AnimateIn>
            </div>
            <div>
              <AnimateIn delay={100}>
                <p className="body-text on-dark">
                  {frameworkContent.aicil.different.body}
                </p>
              </AnimateIn>
            </div>
          </div>

          {/* Production Infrastructure */}
          <div>
            <AnimateIn>
              <h3 className="heading-lg" style={{ color: '#F3F3F1', marginBottom: '32px' }}>
                {frameworkContent.aicil.production.heading}
              </h3>
            </AnimateIn>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
              gap: '24px' 
            }}>
              {frameworkContent.aicil.production.items.map((item, i) => (
                <AnimateIn key={item} delay={i * 100}>
                  <div style={{
                    padding: '24px',
                    border: '1px solid rgba(243,243,241,0.1)',
                    fontFamily: "'Satoshi', sans-serif",
                    fontSize: '15px',
                    color: 'rgba(243,243,241,0.85)'
                  }}>
                    {item}
                  </div>
                </AnimateIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SectionRow number="Four" label="Competitive Moat" />

      {/* Data Moat — Integrated into Light Section */}
      <section className="sec-light sec-pad-lg">
        <div className="inner">
          <AnimateIn>
            <div style={{
              maxWidth: '900px',
              margin: '0 auto',
              padding: '60px 0',
              borderTop: '1px solid rgba(16,16,16,0.1)'
            }}>
              <p style={{
                fontFamily: "'Gambarino', Georgia, serif",
                fontSize: 'clamp(22px, 2.8vw, 40px)',
                lineHeight: '1.3',
                color: '#3E4032'
              }}>
                {frameworkContent.dataMoat}
              </p>
            </div>
          </AnimateIn>
        </div>
      </section>
    </div>
  );
};

export default FrameworkPage;
